import * as React from 'react';
import {
	Children,
	DetailedHTMLProps,
	OptionHTMLAttributes,
	ReactElement,
	ReactNode,
	cloneElement,
	isValidElement,
	useEffect,
	useRef,
	useState
} from 'react';

import { Select, styled } from '@volkswagen-onehub/components-core';
import type { SelectProps } from '@volkswagen-onehub/components-core';

const ELLIPSIS = '\u2026';

const MIN_VISIBLE_CHARS = 2;

function truncateMiddle(charsToTruncate: number, text?: string): string {
	const fullText = text || '';
	const charsToKeep = Math.max(MIN_VISIBLE_CHARS, fullText.length - charsToTruncate);
	if (charsToTruncate <= 0 || charsToKeep >= fullText.length) return fullText;
	const frontChars = Math.ceil(charsToKeep / 2);
	const backChars = Math.floor(charsToKeep / 2);
	return `${fullText.slice(0, frontChars).trim()}${ELLIPSIS}${backChars > 0 ? fullText.slice(-backChars).trim() : ''}`;
}

type OptionEl = ReactElement<DetailedHTMLProps<OptionHTMLAttributes<HTMLOptionElement>, HTMLOptionElement>>;
const isOptionEl = (el: unknown): el is OptionEl => isValidElement(el) && el.type === 'option';

export interface MiddleTruncateSelectProps extends Omit<SelectProps, 'message' | 'appearance'> {
	children?: ReactNode;
	charsToTruncate: number;
}

const ELLIPSIS_SLOP_PX = 2;
const RESERVED_PX = 56;

export const MiddleTruncateSelect = ({ children, charsToTruncate, value, ...rest }: MiddleTruncateSelectProps) => {
	const [shouldTruncate, setShouldTruncate] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const measureRef = useRef<HTMLSpanElement>(null);

	let selectedFullText: string | undefined;
	const childrenArray = Children.toArray(children);
	childrenArray.forEach(ch => {
		if (isOptionEl(ch) && ch.props.value === value && ch.props.children != null) {
			selectedFullText = String(ch.props.children);
		}
	});

	useEffect(() => {
		const root = wrapperRef.current;
		if (!root) return;

		const trigger =
			(root.querySelector('select') as HTMLElement | null) ||
			(root.querySelector('[role="combobox"]') as HTMLElement | null) ||
			(root.querySelector('[aria-haspopup="listbox"]') as HTMLElement | null) ||
			(root.querySelector('[aria-expanded]') as HTMLElement | null);
		if (!trigger) return;

		const open = () => setIsOpen(true);
		const close = () => requestAnimationFrame(() => requestAnimationFrame(() => setIsOpen(false)));

		trigger.addEventListener('mousedown', open);
		trigger.addEventListener('touchstart', open, { passive: true } as AddEventListenerOptions);
		trigger.addEventListener('blur', close);
		trigger.addEventListener('change', close);

		const mo = new MutationObserver(() => {
			const e = trigger.getAttribute('aria-expanded');
			const s = trigger.getAttribute('data-state');
			if (e === 'true' || s === 'open') setIsOpen(true);
			else if (e === 'false' || s === 'closed' || e === null) close();
		});
		mo.observe(trigger, { attributes: true, attributeFilter: ['aria-expanded', 'data-state'] });

		return () => {
			trigger.removeEventListener('mousedown', open);
			trigger.removeEventListener('touchstart', open);
			trigger.removeEventListener('blur', close);
			trigger.removeEventListener('change', close);
			mo.disconnect();
		};
	}, []);

	useEffect(() => {
		const recalc = () => {
			const el = wrapperRef.current;
			const meas = measureRef.current;
			if (!el || !meas || !selectedFullText) {
				setShouldTruncate(false);
				return;
			}
			const available = Math.max(0, el.offsetWidth - RESERVED_PX);
			meas.textContent = selectedFullText;
			const fullW = meas.offsetWidth;
			setShouldTruncate(fullW > available + ELLIPSIS_SLOP_PX);
		};

		recalc();

		let cancelled = false;
		document.fonts?.ready?.then(() => {
			if (!cancelled) recalc();
		});

		const el = wrapperRef.current;
		let ro: ResizeObserver | undefined;

		if (el && typeof ResizeObserver !== 'undefined') {
			ro = new ResizeObserver(() => recalc());
			ro.observe(el);
		}

		const onResize = () => recalc();
		window.addEventListener('resize', onResize);

		return () => {
			cancelled = true;
			if (ro) ro.disconnect();
			window.removeEventListener('resize', onResize);
		};
	}, [selectedFullText, value, childrenArray.length]);

	const renderChildren = () => {
		if (!shouldTruncate || !selectedFullText) return children;
		const truncated = truncateMiddle(charsToTruncate, selectedFullText);
		const textForSelected = isOpen ? selectedFullText : truncated;

		return childrenArray.map(ch => {
			if (!isOptionEl(ch)) return ch;
			if (ch.props.value !== value) return ch;
			return cloneElement<OptionEl['props']>(ch, { key: ch.key ?? ch.props.value }, textForSelected);
		});
	};

	return (
		<Wrapper ref={wrapperRef}>
			<Select {...(rest as SelectProps)} value={value}>
				{renderChildren()}
			</Select>
			<Measure ref={measureRef} aria-hidden role="presentation" />
		</Wrapper>
	);
};

const Wrapper = styled.div`
	position: relative;
	width: 100%;
`;

const Measure = styled.span`
	position: absolute;
	visibility: hidden;
	white-space: pre;
	pointer-events: none;
	font-size: ${({ theme }) => theme.fonts.copy[200].fontSize};
	font-family: ${({ theme }) => theme.fonts.copy[200].fontFamily};
`;
